import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Users,
  FileText,
  Receipt,
  Calendar,
  Wallet,
  Settings,
  LogOut,
  Menu,
  X,
  Sparkles,
  Landmark,
  MessageSquare,
  Camera,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Toaster } from "@/components/ui/sonner";

const nav = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/app/clientes", label: "Clientes", icon: Users, testid: "nav-clientes" },
  { to: "/app/presupuestos", label: "Presupuestos", icon: FileText, testid: "nav-presupuestos" },
  { to: "/app/facturas", label: "Facturas", icon: Receipt, testid: "nav-facturas" },
  { to: "/app/cobros", label: "Cobros", icon: Wallet, testid: "nav-cobros" },
  { to: "/app/banco", label: "Banco", icon: Landmark, testid: "nav-banco" },
  { to: "/app/gastos", label: "Gastos y tickets", icon: Camera, testid: "nav-gastos" },
  { to: "/app/calendario", label: "Calendario Fiscal", icon: Calendar, testid: "nav-calendario" },
  { to: "/app/configuracion", label: "Configuración", icon: Settings, testid: "nav-configuracion" },
];

const adminNav = [
  { to: "/app/mensajes", label: "Mensajes", icon: MessageSquare, testid: "nav-mensajes" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const visibleNav = user?.is_admin ? [...nav, ...adminNav] : nav;

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const planBadge = () => {
    const plan = user?.subscription_plan || "free";
    const map = {
      free: { label: "Gratis", cls: "bg-slate-200 text-slate-700" },
      pro: { label: "Pro", cls: "bg-blue-600 text-white" },
      business: { label: "Business", cls: "bg-indigo-600 text-white" },
    };
    return map[plan] || map.free;
  };

  const plan = planBadge();

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar - desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-[#0F172A] text-slate-100 fixed h-screen">
        <div className="px-6 py-6 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-blue-600 grid place-items-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-lg tracking-tight notranslate" translate="no">
                CHRONOVIA
              </div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest">
                Facturación
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition ${
                  isActive
                    ? "bg-blue-600 text-white"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={2} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-slate-700 grid place-items-center text-sm font-semibold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-slate-400 truncate">
                {user?.email}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span
              className={`text-[10px] px-2 py-1 rounded font-semibold uppercase tracking-wider ${plan.cls}`}
              data-testid="user-plan-badge"
            >
              Plan {plan.label}
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="text-slate-300 hover:text-white hover:bg-slate-800"
              onClick={doLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/70 lg:hidden"
          onClick={() => setMobileOpen(false)}
        >
          <aside
            className="w-64 h-full bg-[#0F172A] text-slate-100 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 flex items-center justify-between border-b border-slate-800">
              <div className="font-display font-bold text-lg notranslate" translate="no">CHRONOVIA</div>
              <button onClick={() => setMobileOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
              {visibleNav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm ${
                      isActive
                        ? "bg-blue-600 text-white"
                        : "text-slate-300 hover:bg-slate-800"
                    }`
                  }
                >
                  <n.icon className="w-4 h-4" />
                  {n.label}
                </NavLink>
              ))}
            </nav>
            <div className="p-4 border-t border-slate-800">
              <Button
                size="sm"
                variant="ghost"
                className="text-slate-300 hover:text-white w-full justify-start"
                onClick={doLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar sesión
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200">
          <div className="flex items-center justify-between px-4 lg:px-8 py-3">
            <button
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6 text-slate-700" />
            </button>
            <div className="hidden lg:block text-sm text-slate-500">
              Bienvenido de nuevo, <b className="text-slate-900">{user?.name}</b>
            </div>
            <div className="lg:hidden font-display font-bold text-slate-900 notranslate" translate="no">
              CHRONOVIA
            </div>
            <Badge
              className={`${plan.cls} border-0`}
              data-testid="header-plan-badge"
            >
              {plan.label}
            </Badge>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 fade-in">
          <Outlet />
        </main>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
